/**
 * Transform API workout program data to the format expected by the prototype UI.
 */

const formatStartDate = (dateString) => {
  if (!dateString) {
    return new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }
  const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateMatch) {
    const [, year, month, day] = dateMatch;
    const normalizedDate = `${year}-${month}-${day}T04:00:00.000Z`;
    const date = new Date(normalizedDate);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }
  return dateString;
};

const convertUTCToLocal = (utcDateString) => {
  if (!utcDateString) return new Date();
  return new Date(utcDateString);
};

const formatDateDisplay = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) return "";
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return `${days[date.getDay()]} ${date.getMonth() + 1}/${date.getDate()}`;
};

const formatTimeDisplay = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) return "";
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const minutesStr = minutes < 10 ? "0" + minutes : minutes;
  return `${hours}:${minutesStr} ${ampm}`;
};

const isToday = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) return false;
  const today = new Date();
  return date.getDate() === today.getDate()
    && date.getMonth() === today.getMonth()
    && date.getFullYear() === today.getFullYear();
};

const calculateEstimatedTime = (blocks) => {
  if (!blocks || blocks.length === 0) return 30;
  return Math.round(blocks.length * 3.5);
};

const countTotalSets = (blocks) => {
  if (!blocks || blocks.length === 0) return 0;
  return blocks.reduce((total, block) => {
    const sets = block.metrics?.find((m) => m.metric_type === "sets");
    const setCount = sets ? parseInt(sets.metric_value_num || sets.value) || 0 : 0;
    return total + setCount;
  }, 0);
};

const getExerciseFocus = (block) => {
  if (!block) return "Strength";
  if (block.notes) return block.notes;
  if (block.prescribed_text) return block.prescribed_text;
  const exerciseName = block.name || block.exercise_name;
  if (exerciseName) {
    const name = exerciseName.toLowerCase();
    if (name.includes("chest") || name.includes("bench")) return "Chest";
    if (name.includes("back") || name.includes("row")) return "Back";
    if (name.includes("squat") || name.includes("leg")) return "Legs";
    if (name.includes("shoulder") || name.includes("press")) return "Shoulders";
  }
  return "Strength";
};

const countExercisesFlat = (blocks) => {
  if (!blocks || blocks.length === 0) return 0;
  return blocks.reduce((total, block) => {
    if (block.children && block.children.length > 0) {
      return total + block.children.length;
    }
    if (block.exercise_name || block.block_type === "exercise") {
      return total + 1;
    }
    return total;
  }, 0);
};

const countTotalSetsFlat = (blocks) => {
  if (!blocks || blocks.length === 0) return 0;
  return blocks.reduce((total, block) => {
    if (block.children && block.children.length > 0) {
      // For containers (superset/circuit), sum child sets or use round_no
      return total + block.children.reduce((childTotal, child) => {
        const sets = child.metrics?.find((m) => m.metric_type === "sets");
        const setCount = sets ? parseInt(sets.metric_value_num || sets.value) || 0 : (block.round_no || 1);
        return childTotal + setCount;
      }, 0);
    }
    const sets = block.metrics?.find((m) => m.metric_type === "sets");
    const setCount = sets ? parseInt(sets.metric_value_num || sets.value) || 0 : 0;
    return total + setCount;
  }, 0);
};

const transformSessionToDay = (session, dayIndex, isUnanchored = false) => {
  const blocks = session.blocks || [];
  const totalSets = countTotalSetsFlat(blocks);
  const estTime = session.duration_minutes || calculateEstimatedTime(blocks);
  const utcDateString = session.workout_date_utc || session.workout_date || session.scheduled_date;
  const localDate = utcDateString ? convertUTCToLocal(utcDateString) : null;
  const dateDisplay = localDate ? formatDateDisplay(localDate) : "";
  const timeDisplay = localDate ? formatTimeDisplay(localDate) : "";
  const isTodaySession = localDate ? isToday(localDate) : false;
  const dayLabel = session.label || session.workout_type || session.session_name || `Session ${dayIndex + 1}`;
  const workoutFocus = session.workout_type || getExerciseFocus(blocks[0]) || "Training";

  // For unanchored: mark the first scheduled session as "next up"
  const isNext = isUnanchored && session.status === "scheduled" && session._isNextSession;

  return {
    day: session.ordinal || dayIndex + 1,
    label: dayLabel,
    focus: workoutFocus,
    exercises: countExercisesFlat(blocks),
    sets: totalSets,
    estTime: estTime,
    completed: session.status === "completed",
    date: dateDisplay,
    time: timeDisplay,
    scheduledDate: localDate,
    isToday: isTodaySession || isNext,
    isNext: isNext,
    sessionId: session.session_id || session.id,
    blocks: blocks,
    status: session.status || "scheduled",
  };
};

const groupSessionsIntoWeeks = (sessions, programStartDate = null, schedulingMode = null) => {
  if (!sessions || sessions.length === 0) return [];

  const isUnanchored = schedulingMode === "unanchored";

  // ─── Unanchored: group by week_number + ordinal ───────────────────────────
  if (isUnanchored) {
    // Sort by week_number then ordinal
    const sorted = [...sessions].sort((a, b) => {
      const wA = a.week_number || 1;
      const wB = b.week_number || 1;
      if (wA !== wB) return wA - wB;
      return (a.ordinal || 0) - (b.ordinal || 0);
    });

    // Mark the first scheduled session as "next"
    let foundNext = false;
    sorted.forEach((s) => {
      if (!foundNext && s.status === "scheduled") {
        s._isNextSession = true;
        foundNext = true;
      }
    });

    const weekMap = new Map();
    sorted.forEach((session) => {
      const wk = session.week_number || 1;
      if (!weekMap.has(wk)) weekMap.set(wk, []);
      weekMap.get(wk).push(session);
    });

    const weeks = [];
    const sortedWeekNumbers = Array.from(weekMap.keys()).sort((a, b) => a - b);
    sortedWeekNumbers.forEach((weekNumber) => {
      const weekSessions = weekMap.get(weekNumber);
      const days = weekSessions.map((session, idx) => transformSessionToDay(session, idx, true));
      weeks.push({
        week: weekNumber,
        label: `Week ${weekNumber}`,
        phase: determinePhase(weekNumber),
        days: days,
      });
    });

    return weeks;
  }

  // ─── Pinned: group by calendar date ───────────────────────────────────────
  const sortedSessions = [...sessions].sort((a, b) => {
    const dateA = new Date(a.workout_date_utc || a.workout_date || a.scheduled_date || 0);
    const dateB = new Date(b.workout_date_utc || b.workout_date || b.scheduled_date || 0);
    return dateA - dateB;
  });

  let startDate;
  if (programStartDate) startDate = new Date(programStartDate);
  else if (sortedSessions[0]?.workout_date_utc || sortedSessions[0]?.workout_date || sortedSessions[0]?.scheduled_date) {
    startDate = new Date(sortedSessions[0].workout_date_utc || sortedSessions[0].workout_date || sortedSessions[0].scheduled_date);
  } else startDate = new Date();

  startDate.setHours(0, 0, 0, 0);
  const startDayOfWeek = startDate.getDay();
  const weekStartDate = new Date(startDate);
  weekStartDate.setDate(startDate.getDate() - startDayOfWeek);

  const weekMap = new Map();
  sortedSessions.forEach((session) => {
    const sessionDateString = session.workout_date_utc || session.workout_date || session.scheduled_date;
    const sessionDate = sessionDateString ? new Date(sessionDateString) : new Date();
    sessionDate.setHours(0, 0, 0, 0);
    const daysSinceWeekStart = Math.floor((sessionDate - weekStartDate) / (1000 * 60 * 60 * 24));
    const weekNumber = Math.floor(daysSinceWeekStart / 7) + 1;
    if (!weekMap.has(weekNumber)) weekMap.set(weekNumber, []);
    weekMap.get(weekNumber).push(session);
  });

  const weeks = [];
  const sortedWeekNumbers = Array.from(weekMap.keys()).sort((a, b) => a - b);
  sortedWeekNumbers.forEach((weekNumber) => {
    const weekSessions = weekMap.get(weekNumber);
    const days = weekSessions.map((session, idx) => transformSessionToDay(session, idx, false));
    weeks.push({
      week: weekNumber,
      label: `Week ${weekNumber}`,
      phase: determinePhase(weekNumber),
      days: days,
    });
  });

  return weeks;
};

const determinePhase = (weekNumber) => {
  if (weekNumber <= 2) return "Stabilization";
  if (weekNumber <= 4) return "Strength Endurance";
  if (weekNumber <= 6) return "Hypertrophy";
  return "Max Strength";
};

const generatePhases = (totalWeeks) => {
  const phases = [];
  if (totalWeeks >= 2) phases.push({ name: "Stabilization", weeks: [1, 2], description: "Building your foundation. Core stability, movement quality, and joint prep.", focus: ["Balance", "Core activation", "Movement patterns", "Joint stability"] });
  if (totalWeeks >= 4) phases.push({ name: "Strength Endurance", weeks: [3, 4], description: "Higher reps, moderate weight. Building work capacity and muscular endurance.", focus: ["Supersets", "Moderate load", "12–15 rep range", "Minimal rest"] });
  if (totalWeeks >= 6) phases.push({ name: "Hypertrophy", weeks: [5, 6], description: "Time to grow. Increased volume, controlled tempos, progressive overload.", focus: ["Volume increase", "8–12 rep range", "Tempo training", "Mind-muscle connection"] });
  if (totalWeeks >= 8) phases.push({ name: "Max Strength", weeks: [7, 8], description: "Peak performance. Heavy loads, low reps, full recovery.", focus: ["Heavy compounds", "3–6 rep range", "Full recovery", "PR attempts"] });
  return phases;
};

const generatePhaseTimeline = (totalWeeks) => {
  const t = [];
  if (totalWeeks >= 2) t.push({ weeks: [1, 2], label: "Stabilization" });
  if (totalWeeks >= 4) t.push({ weeks: [3, 4], label: "Strength Endurance" });
  if (totalWeeks >= 6) t.push({ weeks: [5, 6], label: "Hypertrophy" });
  if (totalWeeks >= 8) t.push({ weeks: [7, 8], label: "Max Strength" });
  return t;
};

export const transformToProgramPreview = (apiProgram) => {
  const programMeta = apiProgram.program || apiProgram;
  const sessions = apiProgram.sessions || programMeta.sessions || [];
  const schedulingMode = programMeta.scheduling_mode || apiProgram.scheduling_mode || null;
  const weeks = groupSessionsIntoWeeks(sessions, programMeta.start_date || apiProgram.start_date, schedulingMode);
  const totalWeeks = weeks.length;

  return {
    id: apiProgram.id,
    name: programMeta.name || programMeta.program_name || "Training Program",
    coach: programMeta.coach_name || apiProgram.coach_name || "Your Coach",
    client: programMeta.client_name || apiProgram.client_name || "Client",
    totalWeeks: totalWeeks,
    daysPerWeek: 5,
    startDate: programMeta.start_date || apiProgram.start_date || new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }),
    sentAt: new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }),
    coachMessage: programMeta.description || apiProgram.description || apiProgram.notes || "Here is your training program. Follow the plan and track your progress.",
    overview: {
      totalSessions: sessions.length,
      estHoursTotal: Math.round(sessions.reduce((total, s) => total + calculateEstimatedTime(s.blocks || []), 0) / 60),
      equipment: "Full gym access recommended",
    },
    phases: generatePhases(totalWeeks),
    weeks: weeks,
    schedulingMode: schedulingMode,
  };
};

export const transformToProgramDashboard = (apiProgram, currentWeek = null) => {
  // Handle V2 shape: { program: {...}, sessions: [...] }
  const programMeta = apiProgram.program || apiProgram;
  const sessions = apiProgram.sessions || programMeta.sessions || [];
  const schedulingMode = programMeta.scheduling_mode || apiProgram.scheduling_mode || null;
  const isUnanchored = schedulingMode === "unanchored";
  const weeks = groupSessionsIntoWeeks(sessions, programMeta.start_date || apiProgram.start_date, schedulingMode);

  let detectedCurrentWeek = currentWeek;
  if (!detectedCurrentWeek) {
    let foundWeek = null;

    if (isUnanchored) {
      // For unanchored: current week = the week containing the first scheduled session
      for (const week of weeks) {
        const hasScheduled = week.days.some((d) => !d.completed);
        if (hasScheduled) { foundWeek = week.week; break; }
      }
    } else {
      // For pinned: current week = the week with today's date
      for (const week of weeks) {
        for (const day of week.days) {
          if (day.scheduledDate && isToday(day.scheduledDate)) { foundWeek = week.week; break; }
        }
        if (foundWeek) break;
      }
      if (!foundWeek) {
        for (const week of weeks) {
          const hasIncomplete = week.days.some((d) => !d.completed);
          if (hasIncomplete) { foundWeek = week.week; break; }
        }
      }
    }

    detectedCurrentWeek = foundWeek || 1;
    if (detectedCurrentWeek > weeks.length) detectedCurrentWeek = weeks.length;
    if (detectedCurrentWeek < 1) detectedCurrentWeek = 1;
  }

  const currentWeekData = weeks[detectedCurrentWeek - 1];
  const completedInCurrentWeek = currentWeekData?.days.filter((d) => d.completed).length || 0;
  const currentDay = completedInCurrentWeek;

  const weeksWithToday = weeks.map((week) => ({
    ...week,
    days: week.days.map((day) => ({
      ...day,
      isToday: isUnanchored ? day.isNext || false : (day.scheduledDate ? isToday(day.scheduledDate) : false),
    })),
  }));

  let displayStartDate = programMeta.start_date || apiProgram.start_date;
  if (!isUnanchored && weeksWithToday.length > 0 && weeksWithToday[0].days.length > 0) {
    const firstDay = weeksWithToday[0].days[0];
    if (firstDay.scheduledDate) {
      const dateOnly = firstDay.scheduledDate.toISOString().split("T")[0];
      displayStartDate = `${dateOnly}T04:00:00.000Z`;
    }
  }

  return {
    id: programMeta.id || apiProgram.program_id || apiProgram.id,
    name: programMeta.name || programMeta.program_name || "Training Program",
    coach: programMeta.coach_name || apiProgram.coach_name || "Your Coach",
    client: programMeta.client_name || apiProgram.client_name || "Client",
    totalWeeks: weeks.length,
    currentWeek: detectedCurrentWeek,
    currentDay: currentDay,
    startDate: formatStartDate(displayStartDate),
    phase: generatePhaseTimeline(weeks.length),
    weeks: weeksWithToday,
    schedulingMode: schedulingMode,
  };
};

export const findActiveProgram = (programs) => {
  let programsArray = programs;
  if (!programs) return null;
  if (!Array.isArray(programs)) {
    if (programs.programs && Array.isArray(programs.programs)) programsArray = programs.programs;
    else if (programs.data && Array.isArray(programs.data)) programsArray = programs.data;
    else if (programs.results && Array.isArray(programs.results)) programsArray = programs.results;
    else return programs;
  }
  if (programsArray.length === 0) return null;

  const activeProgram = programsArray.find((p) => p.status === "active" || p.status === "in_progress");
  if (activeProgram) return activeProgram;

  const nonCompletedProgram = programsArray.find((p) => p.status !== "completed");
  if (nonCompletedProgram) return nonCompletedProgram;

  return programsArray[0];
};
